/**
 * Singleton WebSocket client for live chat + notifications.
 *
 * Connection: ws(s)://<REACT_APP_BACKEND_URL>/api/ws
 *   - Cookies (`access_token` / `session_token`) ride along automatically because
 *     the browser includes them on same-origin WebSocket handshakes.
 *
 * Channel naming (must match backend):
 *   planet:{planet_id}   → planet room messages
 *   dm:{thread_key}      → DM messages (thread_key = sorted user ids joined by ::)
 *   user:{user_id}       → personal events (notifications, badge updates)
 *
 * Public API:
 *   ws.subscribe(channel, handler)   → returns unsubscribe()
 *   ws.send({type:'ping'})           → low-level send
 *   ws.dmThreadKey(a, b)             → helper to build a dm: thread key
 */
import { useEffect } from 'react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
// http(s) → ws(s)
const WS_URL = BACKEND.replace(/^http/i, 'ws') + '/api/ws';

class LiveSocket {
  constructor() {
    this.sock = null;
    this.connected = false;
    this.connecting = false;
    this.queue = [];                          // outbound messages buffered while connecting
    this.handlers = new Map();                // channel -> Set<handler>
    this.subCounts = new Map();               // channel -> int (ref count)
    this.retryDelay = 1000;                   // grows up to 15s
    this.heartbeat = null;
    this.disposed = false;
    this.lastActivity = 0;                    // last inbound message (incl. pong) — used by the stale-socket watchdog

    // Tab visibility watchdog — mobile browsers freeze/background sockets
    // without firing onclose, leaving a half-open socket that silently
    // swallows messages. On tab re-focus we force a reconnect if the
    // socket looks dead or stale (>30s without any inbound traffic).
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return;
        const stale = !this.lastActivity || Date.now() - this.lastActivity > 30000;
        if (!this.connected || stale) this.reconnectNow();
      });
    }
  }

  /** Force-drop the current socket and reconnect with fresh backoff. */
  reconnectNow() {
    this.retryDelay = 1000;
    if (this.sock) {
      try { this.sock.close(); } catch { /* noop */ }
      // onclose → teardown → _scheduleReconnect handles the rest
      return;
    }
    this._ensureConnected();
  }

  _ensureConnected() {
    if (this.connected || this.connecting || this.disposed) return;
    this.connecting = true;
    let s;
    try { s = new WebSocket(WS_URL); }
    catch { this.connecting = false; this._scheduleReconnect(); return; }
    this.sock = s;

    s.onopen = () => {
      this.connected = true;
      this.connecting = false;
      this.retryDelay = 1000;
      // Re-subscribe to every active channel
      for (const ch of this.subCounts.keys()) {
        this._raw({ type: 'subscribe', channel: ch });
      }
      // Flush queued sends
      const q = this.queue; this.queue = [];
      for (const m of q) this._raw(m);
      // Heartbeat every 25s + stale-socket watchdog: if we've been
      // "connected" for over 60s without ANY inbound frame (not even our
      // own pong), the socket is half-open — close it so teardown kicks
      // off a fresh reconnect instead of silently losing messages.
      this.lastActivity = Date.now();
      this.heartbeat = setInterval(() => {
        if (this.connected && Date.now() - this.lastActivity > 60000) {
          try { this.sock?.close(); } catch { /* noop */ }
          return;
        }
        this._raw({ type: 'ping' });
      }, 25000);
    };

    s.onmessage = (ev) => {
      this.lastActivity = Date.now();
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      const ch = msg.channel;
      if (!ch) return;
      const set = this.handlers.get(ch);
      if (!set) return;
      for (const fn of set) { try { fn(msg); } catch { /* ignore */ } }
    };

    const teardown = () => {
      this.connected = false;
      this.connecting = false;
      this.sock = null;
      if (this.heartbeat) { clearInterval(this.heartbeat); this.heartbeat = null; }
      if (!this.disposed) this._scheduleReconnect();
    };
    s.onclose = teardown;
    s.onerror = () => { try { s.close(); } catch { /* noop */ } };
  }

  _scheduleReconnect() {
    setTimeout(() => this._ensureConnected(), this.retryDelay);
    this.retryDelay = Math.min(this.retryDelay * 2, 15000);
  }

  _raw(m) {
    if (this.sock && this.connected) {
      try { this.sock.send(JSON.stringify(m)); return true; } catch { /* fallthrough */ }
    }
    this.queue.push(m);
    return false;
  }

  /** Subscribe to a channel. Returns an unsubscribe function. */
  subscribe(channel, handler) {
    if (!channel || typeof handler !== 'function') return () => {};
    let set = this.handlers.get(channel);
    if (!set) { set = new Set(); this.handlers.set(channel, set); }
    set.add(handler);
    const prev = this.subCounts.get(channel) || 0;
    this.subCounts.set(channel, prev + 1);
    if (prev === 0) this._raw({ type: 'subscribe', channel });
    this._ensureConnected();

    return () => {
      const s = this.handlers.get(channel);
      if (s) {
        s.delete(handler);
        if (s.size === 0) this.handlers.delete(channel);
      }
      const c = (this.subCounts.get(channel) || 1) - 1;
      if (c <= 0) {
        this.subCounts.delete(channel);
        this._raw({ type: 'unsubscribe', channel });
      } else {
        this.subCounts.set(channel, c);
      }
    };
  }

  dmThreadKey(a, b) {
    if (!a || !b) return '';
    return [a, b].sort().join('::');
  }
}

const ws = new LiveSocket();
export default ws;

/** React helper: subscribe for the lifetime of a component. */
export function useChannel(channel, handler, deps = []) {
  useEffect(() => {
    if (!channel) return undefined;
    return ws.subscribe(channel, handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, ...deps]);
}
